import { useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import StorageService from '@services/storage'
import { useAppSelector } from '@hooks/reduxHooks'

const BROADCAST_CHANNEL_NAME = 'report-sync'
const WINDOW_BOUNDS_KEY = 'report-window-bounds'

const ReportShellPage = () => {
  const [searchParams] = useSearchParams()
  const reportUrl = searchParams.get('url')
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const channelRef = useRef<BroadcastChannel | null>(null)
  const lastBoundsRef = useRef<string>('') // Track last saved to avoid unnecessary writes

  const { user, account } = useAppSelector((state) => ({
    user: state.auth.user,
    account: state.auth.account,
  }))
  useEffect(() => {
    // Try to restore position on mount
    try {
      const saved = localStorage.getItem(WINDOW_BOUNDS_KEY)
      if (saved) {
        const bounds = JSON.parse(saved)
        console.log('[ReportShell] Restoring position:', bounds)

        // Move window to saved position
        window.moveTo(bounds.x, bounds.y)
        window.resizeTo(bounds.width, bounds.height)
      }
    } catch (e) {
      console.warn('[ReportShell] Could not restore position:', e)
    }

    // Save position periodically
    const savePosition = () => {
      const bounds = {
        x: window.screenX,
        y: window.screenY,
        width: window.outerWidth,
        height: window.outerHeight,
      }

      const boundsString = JSON.stringify(bounds)

      // Only save if changed (reduces localStorage writes)
      if (boundsString !== lastBoundsRef.current) {
        lastBoundsRef.current = boundsString
        localStorage.setItem(WINDOW_BOUNDS_KEY, boundsString)
        console.log('[ReportShell] Saved position:', bounds)
      }
    }

    const interval = setInterval(savePosition, 500) // More frequent for better tracking
    window.addEventListener('beforeunload', savePosition)

    return () => {
      clearInterval(interval)
      window.removeEventListener('beforeunload', savePosition)
    }
  }, [])
  useEffect(() => {
    channelRef.current = new BroadcastChannel(BROADCAST_CHANNEL_NAME)

    channelRef.current.onmessage = (event) => {
      const iframe = iframeRef.current?.contentWindow
      if (iframe) {
        iframe.postMessage(event.data, '*')
      }
    }

    channelRef.current.postMessage({
      type: 'SHELL_READY',
      url: reportUrl,
    })

    const handleBeforeUnload = () => {
      channelRef.current?.postMessage({ type: 'SHELL_CLOSED' })
    }
    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      channelRef.current?.close()
    }
  }, [reportUrl])
  const handleIframeMessage = useCallback(
    (event: MessageEvent) => {
      const message = event.data
      if (!message || !message.type || !message.id) return

      // Handle auth locally
      if (message.type === 'get-auth-token') {
        try {
          const token = StorageService.getAuthToken()
          const response = {
            id: message.id,
            data: { token, user, account },
          }
          ;(event.source as Window)?.postMessage(response, '*')
        } catch (error) {
          (event.source as Window)?.postMessage(
            {
              id: message.id,
              data: null,
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to get auth token',
            },
            '*',
          )
        }
        return
      }

      // Forward everything else to main app
      channelRef.current?.postMessage({
        ...message,
        _fromShell: true,
      })
    },
    [user, account],
  )

  useEffect(() => {
    window.addEventListener('message', handleIframeMessage)
    return () => window.removeEventListener('message', handleIframeMessage)
  }, [handleIframeMessage])

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────
  if (!reportUrl) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontFamily: 'system-ui, sans-serif',
          color: '#666',
        }}
      >
        <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>
          Aucun rapport sélectionné
        </h1>
        <p>Cette page doit être ouverte avec un paramètre ?url=</p>
      </div>
    )
  }

  return (
    <iframe
      ref={iframeRef}
      src={reportUrl}
      allow="microphone"
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        display: 'block',
      }}
      title="Report Editor"
    />
  )
}

export default ReportShellPage
